import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { CritBadge, StatusBadge, EmptyState } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Boxes, Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", criticality: "medium", rto_hours: 24, rpo_hours: 24, sla: "", owner: "", status: "active", description: "" };

export default function Services() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["services"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, rto_hours: Number(form.rto_hours), rpo_hours: Number(form.rpo_hours) };
      return editing ? (await api.put(`/services/${editing}`, body)).data : (await api.post("/services", body)).data;
    },
    onSuccess: () => { toast.success(editing ? "Service mis à jour" : "Service créé"); setOpen(false); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/services/${id}`),
    onSuccess: () => { toast.success("Service supprimé"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ name: s.name, ...EMPTY, ...s.attributes }); setOpen(true); };
  const writable = can("service:write");

  return (
    <AppShell title="Services critiques" subtitle={`${rows.length} service(s) — projection type=service`}
      actions={writable && (
        <Button size="sm" className="rounded-sm gap-2" onClick={openCreate} data-testid="create-service-button">
          <Plus className="h-4 w-4" /> Nouveau service
        </Button>
      )}>
      {isLoading ? (
        <p className="text-sm text-muted-foreground font-mono">Chargement…</p>
      ) : rows.length === 0 ? (
        <EmptyState icon={Boxes} title="Aucun service critique" hint="Déclarez vos services métier essentiels avec leurs RTO/RPO." />
      ) : (
        <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="services-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>Nom</TableHead>
                <TableHead>Criticité</TableHead>
                <TableHead>RTO (h)</TableHead>
                <TableHead>RPO (h)</TableHead>
                <TableHead>SLA</TableHead>
                <TableHead>Propriétaire</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} className="border-border cursor-pointer hover:bg-secondary/40"
                  onClick={() => navigate(`/services/${s.id}`)} data-testid={`service-row-${s.id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell><CritBadge value={s.attributes.criticality} /></TableCell>
                  <TableCell className="font-mono text-xs">{s.attributes.rto_hours}</TableCell>
                  <TableCell className="font-mono text-xs">{s.attributes.rpo_hours}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{s.attributes.sla || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{s.attributes.owner || "—"}</TableCell>
                  <TableCell><StatusBadge value={s.attributes.status} /></TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {writable && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => { if (window.confirm("Supprimer ?")) del.mutate(s.id); }} data-testid={`delete-service-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{editing ? "Modifier le service" : "Nouveau service"}</DialogTitle>
            <DialogDescription>Service métier critique avec objectifs RTO/RPO.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label className="text-xs">Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="service-name-input" />
            </div>
            <div>
              <Label className="text-xs">Criticité</Label>
              <Select value={form.criticality} onValueChange={(v) => setForm({ ...form, criticality: v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critique</SelectItem>
                  <SelectItem value="high">Élevé</SelectItem>
                  <SelectItem value="medium">Moyen</SelectItem>
                  <SelectItem value="low">Faible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="degraded">Dégradé</SelectItem>
                  <SelectItem value="offline">Hors ligne</SelectItem>
                  <SelectItem value="planned">Planifié</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">RTO (heures)</Label>
              <Input type="number" min="0" value={form.rto_hours} onChange={(e) => setForm({ ...form, rto_hours: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs">RPO (heures)</Label>
              <Input type="number" min="0" value={form.rpo_hours} onChange={(e) => setForm({ ...form, rpo_hours: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs">SLA</Label>
              <Input value={form.sla} onChange={(e) => setForm({ ...form, sla: e.target.value })} className="mt-1 rounded-sm font-mono" placeholder="ex: 99,9 %" data-testid="service-sla-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Propriétaire</Label>
              <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="save-service-button">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
